// fronted/src/components/admin/layout/AdminSidebar.js
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import {
  FiBell,
  FiCalendar,
  FiChevronLeft,
  FiDollarSign,
  FiHome,
  FiLogOut,
  FiMail,
  FiRefreshCw,
  FiUser,
  FiUsers
} from "react-icons/fi";
import { NavLink, useNavigate } from "react-router-dom";
import { useAdmin } from "../../../contexts/AdminContext";
import Logo from "../../../images/logos/logo.png";

const AdminSidebar = () => {
  const { logout } = useAdmin();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const navItems = [
    { 
      name: 'Dashboard', 
      path: '/admin', 
      icon: <FiHome />, 
      color: 'from-blue-500 to-cyan-500',
      description: 'Overview & Analytics'
    },
    { 
      name: 'Announcements', 
      path: '/admin/announcements', 
      icon: <FiBell />, 
      color: 'from-purple-500 to-pink-500',
      description: 'Church Updates',
    },
    { 
      name: 'Events', 
      path: '/admin/events', 
      icon: <FiCalendar />, 
      color: 'from-green-500 to-emerald-500',
      description: 'Schedule & Calendar'
    },
    { 
      name: 'Ministries', 
      path: '/admin/ministries', 
      icon: <FiUsers />, 
      color: 'from-indigo-500 to-blue-500',
      description: 'Ministry Groups'
    },
    { 
      name: 'Cell Groups', 
      path: '/admin/cell-groups', 
      icon: <FiUsers />, 
      color: 'from-teal-500 to-cyan-500',
      description: 'Small Groups'
    },
    { 
      name: 'Payments', 
      path: '/admin/payments', 
      icon: <FiDollarSign />, 
      color: 'from-yellow-500 to-orange-500',
      description: 'Donations & Tithes'
    },
    { 
      name: 'Messages', 
      path: '/admin/messages', 
      icon: <FiMail />, 
      color: 'from-red-500 to-pink-500',
      description: 'Contact Inquiries',
    },
    { 
      name: 'Updates', 
      path: '/admin/updates', 
      icon: <FiRefreshCw />, 
      color: 'from-violet-500 to-purple-500',
      description: 'System Updates'
    },
    { 
      name: 'Leaders', 
      path: '/admin/leaders', 
      icon: <FiUser />, 
      color: 'from-amber-500 to-yellow-500',
      description: 'Leadership Team'
    },
  ];

  const sidebarVariants = {
    expanded: { width: 280 },
    collapsed: { width: 80 }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <motion.div
      variants={sidebarVariants}
      animate={isCollapsed ? "collapsed" : "expanded"}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="relative flex flex-col backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-r border-white/20 dark:border-gray-700/30 shadow-2xl"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative p-6 border-b border-white/20 dark:border-gray-700/30"
      >
        <div className="flex items-center justify-between">
          <AnimatePresence>
            {!isCollapsed && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="flex items-center space-x-3"
              >
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-lg"><img src={Logo} alt="logo"/></span>
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                    Admin Dashboard
                  </h1>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-lg bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 hover:from-gray-200 hover:to-gray-300 dark:hover:from-gray-600 dark:hover:to-gray-700 transition-all duration-200 shadow-md"
          >
            <motion.div
              animate={{ rotate: isCollapsed ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <FiChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </motion.div>
          </motion.button>
        </div>
      </motion.div>
      
      {/* Navigation */}
      <nav className="flex-1 py-6 overflow-y-auto scrollbar-hide">
        <div className="space-y-2 px-4">
          {navItems.map((item, index) => (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <NavLink
                to={item.path}
                className={({ isActive }) => 
                  `group relative flex items-center rounded-xl transition-all duration-300 ${
                    isActive 
                      ? 'bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 shadow-lg scale-105' 
                      : 'hover:bg-white/60 dark:hover:bg-gray-800/60 hover:shadow-md hover:scale-102'
                  } ${isCollapsed ? 'p-3 justify-center' : 'p-4'}`
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Active indicator */}
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-purple-500 rounded-r-full"
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    )}
                    
                    {/* Icon */}
                    <div className={`relative flex items-center justify-center ${isCollapsed ? '' : 'mr-4'}`}>
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className={`p-2.5 rounded-lg ${
                          isActive 
                            ? `bg-gradient-to-r ${item.color} text-white shadow-lg` 
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-gray-700'
                        } transition-all duration-200`}
                      >
                        {item.icon}
                      </motion.div>
                      
                      {/* Badge */}
                      {item.badge && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg"
                        >
                          <span className="text-white text-xs font-bold">{item.badge}</span>
                        </motion.div>
                      )}
                    </div>
                    
                    {/* Text Content */}
                    <AnimatePresence>
                      {!isCollapsed && (
                        <motion.div
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.2 }}
                          className="flex-1 min-w-0"
                        >
                          <div className={`font-semibold text-sm ${
                            isActive 
                              ? 'text-gray-900 dark:text-white' 
                              : 'text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white'
                          } transition-colors duration-200`}>
                            {item.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {item.description}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </NavLink>
            </motion.div>
          ))}
        </div>
      </nav>
      
      {/* Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="p-4 border-t border-white/20 dark:border-gray-700/30 space-y-3"
      >
        <motion.button 
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleLogout}
          className={`group w-full flex items-center rounded-xl p-3 bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 hover:from-red-100 hover:to-pink-100 dark:hover:from-red-900/30 dark:hover:to-pink-900/30 border border-red-200/50 dark:border-red-800/50 transition-all duration-200 shadow-sm hover:shadow-md ${
            isCollapsed ? 'justify-center' : ''
          }`}
        >
          <motion.div
            whileHover={{ rotate: 15 }}
            className="p-2 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-md"
          >
            <FiLogOut className="h-4 w-4" />
          </motion.div>
          
          <AnimatePresence>
            {!isCollapsed && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="ml-3 flex-1 text-left"
              >
                <div className="font-semibold text-sm text-red-600 dark:text-red-400">
                  Sign Out
                </div>
                <div className="text-xs text-red-500 dark:text-red-500">
                  End session
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
        
        <AnimatePresence>
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="text-center"
            >
              <div className="text-xs text-gray-500 dark:text-gray-400">
                © 2024 ELM Church
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default AdminSidebar;